import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { accountBlock } from '@/lib/account-access'
import { isPublicRegistrationEnabled } from '@/utils/public-registration-switch'

export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore()

  // Check if route explicitly doesn't require auth
  if (to.meta.requiresAuth === false) {
    next()
    return
  }

  // Public routes - always allow access
  const publicRoutes = ['/login', '/callback']
  if (publicRoutes.includes(to.path)) {
    next()
    return
  }

  // Only check auth state if not already authenticated
  // This prevents redundant checks on every navigation
  // The token refresh composable handles keeping the session alive
  if (!authStore.isAuthenticated) {
    // Wait for auth to be checked/restored from session
    // This is critical for page refreshes to not lose auth state
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    // Redirect to login with returnTo parameter
    next({
      path: '/login',
      query: { returnTo: to.fullPath },
    })
    return
  }

  // The member's organisation may not operate (#1809) — the BFF's access gate refuses every call
  // they make. Checked BEFORE the permission and role checks below on purpose: the gate also
  // refuses their roles load, so those checks would fail and send them to /forbidden, which blames
  // their permissions and never says to contact Site Compass. The destination page is
  // `requiresAuth: false`, so it is let through at the top of this guard and cannot loop.
  //
  // Only while the switch is on (#2568): the destination is registered only then. With it off
  // the flag can still be set if the two halves disagree (BFF on, SPA off), and redirecting to
  // an unregistered path would bring the guard straight back here with the flag still set.
  if (authStore.organisationUnavailable && isPublicRegistrationEnabled()) {
    next({ path: '/organisation-unavailable' })
    return
  }

  // The member holds no usable seat (#1848): an own-API call was refused with SEAT_REQUIRED and
  // the fetch watch (lib/account-access.ts) raised the flag. Their roles load is never refused,
  // so without this they would see refused calls on every page. Same switch rule and same
  // reason as above: the page exists only while the switch is on, and is `requiresAuth: false`.
  // A seatless organisation admin may still reach the seat-billing pages, to buy or assign seats.
  const seatBillingPage = authStore.hasRole('organisation-admin') && to.path.startsWith('/billing/')
  if (authStore.seatRequired && isPublicRegistrationEnabled() && !seatBillingPage) {
    next({ path: '/no-seat' })
    return
  }

  // Access to the token's organisation is blocked (ACCOUNT_DISABLED / MEMBERSHIP_REMOVED, see
  // lib/account-access.ts). Like the check above it runs before the role checks, but lets
  // navigation through instead of redirecting. The BFF refuses the roles load too, so the role
  // checks below would fail and a reload of an admin-only route (F5 on /admin/users) would land on
  // /forbidden, which blames their permissions. Skip them and let navigation proceed: App.vue
  // renders AccountBlockedScreen in place of the page, so nothing of the page is shown.
  if (accountBlock.value) {
    next()
    return
  }

  // Check permissions if route requires them
  const requiredPermission = to.meta.permission as string | undefined
  if (requiredPermission && !authStore.hasPermission(requiredPermission)) {
    next({ path: '/forbidden' })
    return
  }

  // Check roles if route requires them (database roles, not Auth0 permissions)
  const requiredRole = to.meta.role as string | undefined
  if (requiredRole && !authStore.hasRole(requiredRole)) {
    next({ path: '/forbidden' })
    return
  }

  // Check for multiple roles (if any role matches, allow access)
  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles && requiredRoles.length > 0) {
    const hasAnyRole = requiredRoles.some(role => authStore.hasRole(role))
    if (!hasAnyRole) {
      next({ path: '/forbidden' })
      return
    }
  }

  // User is authenticated and has required permissions
  next()
}
